import { useEffect, useState } from 'react';
import type { Onboarding } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { TextAreaField, TextField } from '../../components/ui/Field.js';
import { useRegenerateWelcomeLink, useSendWelcomeEmail } from './api.js';

const DEFAULT_CC = 'pmo@nscatalyst.com';

interface SendWelcomeEmailModalProps {
  isOpen: boolean;
  onboarding: Onboarding;
  onClose: () => void;
}

function welcomeUrlOf(onboarding: Onboarding): string {
  if (onboarding.welcomeToken && onboarding.welcomeEnabled && typeof window !== 'undefined') {
    return `${window.location.origin}/w/${onboarding.welcomeToken}`;
  }
  return '';
}

/**
 * When the customer has OTHER in-progress onboardings, the pending-engagements list =
 * this engagement plus the others; empty (section omitted) when this is the only one.
 */
function pendingEngagements(onboarding: Onboarding): string[] {
  if (onboarding.activeSiblings.length === 0) return [];
  return [onboarding.opportunityName, ...onboarding.activeSiblings.map((s) => s.opportunityName)];
}

function buildDefaultBody(
  firstName: string,
  url: string,
  groupEmail: string,
  opportunityName: string,
  opportunityType: string,
  engagements: string[],
): string {
  const greeting = firstName ? `Hi ${firstName},` : 'Hi,';
  const link = url || '[generate the welcome link first to insert it here]';
  const adminEmail = groupEmail || '[create the MS group first to insert the group email]';
  const engagement = opportunityName || 'this engagement';
  const engagementsBlock =
    engagements.length > 0
      ? `\nWe see that you have the following engagements pending with us:\n\n${engagements.join('\n')}\n\nThis process will get us ready for all of them.\n`
      : '';
  return `${greeting}

Welcome to the Catalyst family! We're genuinely excited to kick off ${engagement} with you and can't wait to get to work.
${engagementsBlock}
To make sure we hit the ground running, we have just a couple of quick items to get squared away on your end:

1. Complete our onboarding form:

This short form will capture your billing address, billing and project contacts, support contacts, and your NetSuite status and ID — everything we need to get your project set up on our side.

${link}

2. Create an admin user in your NetSuite instance
Please create a new user with the email address ${adminEmail} and assign it the Administrator role. This gives our team the access needed to get to work in your system right away. (Step-by-step instructions at the bottom of this email)

That's it for now! Once we have those two items, we'll have everything we need to get your project rolling. In the meantime, don't hesitate to reach out if you have any questions — we're already in your corner.

Looking forward to a great partnership!

Best,
Catalyst



Step 1 — Create the Employee Record
Navigate to Lists > Employees > Employees > New
Fill in the required fields:
First Name / Last Name — "Catalyst Consultants"
Email — Enter ${adminEmail}

Step 2 — Set Up NetSuite Access (Give Login Credentials)
While still on the Employee record, click the Access tab (or Human Resources > Access subtab depending on your NetSuite version)
Check the box "Give Access" — this enables the employee to log into NetSuite
Set a Password (or check "Send New Password" to have NetSuite email login instructions to ${adminEmail})

Step 3 — Assign the Administrator Role
Still on the Access tab, scroll down to the Roles sublist
Click Add to add a new role line
In the Role dropdown, select Administrator
Save the employee record

Step 4 — The Employee Receives a Welcome Email
Once saved:
If you checked "Send New Password", NetSuite will automatically send a welcome/login email to us with our credentials and a link to log in.
We will be prompted to change our password on first login.${opportunityType ? `\n\nEngagement type: ${opportunityType}` : ''}`;
}

/**
 * Returning customers (a NetSuite Account ID is already on file) get a shorter body
 * with no NetSuite admin-user setup instructions.
 */
function buildReturningCustomerBody(
  firstName: string,
  url: string,
  opportunityName: string,
  opportunityType: string,
  engagements: string[],
): string {
  const greeting = firstName ? `Hi ${firstName},` : 'Hi,';
  const link = url || '[generate the welcome link first to insert it here]';
  const engagement = opportunityName || 'this engagement';
  const engagementsBlock =
    engagements.length > 0
      ? `\nWe see that you have the following engagements pending with us:\n\n${engagements.join('\n')}\n\nThis process will get us ready for all of them.\n`
      : '';
  return `${greeting}

We appreciate your business. We're excited to kick off ${engagement} with you and look forward to working with your team again.
${engagementsBlock}
To make sure we hit the ground running, we ask that you fill out this quick form. It will help us capture everything we need to get your project set up on our side and who to reach out to on your side.

${link}

Best,
Catalyst${opportunityType ? `\n\nEngagement type: ${opportunityType}` : ''}`;
}

/** A customer with a NetSuite Account ID on file is a returning customer. */
function isReturningCustomer(onboarding: Onboarding): boolean {
  return Boolean(onboarding.customerNetsuiteAccountId?.trim());
}

function defaultSubject(onboarding: Onboarding): string {
  return isReturningCustomer(onboarding)
    ? `Thank you for your business, ${onboarding.customerName}`
    : `Welcome to Catalyst, ${onboarding.customerName}`;
}

function defaultBody(onboarding: Onboarding, url: string): string {
  const firstName = (onboarding.opportunityContactName ?? '').split(' ')[0] ?? '';
  return isReturningCustomer(onboarding)
    ? buildReturningCustomerBody(
        firstName,
        url,
        onboarding.opportunityName,
        onboarding.opportunityServiceCategoryName ?? '',
        pendingEngagements(onboarding),
      )
    : buildDefaultBody(
        firstName,
        url,
        onboarding.msGroupEmail ?? '',
        onboarding.opportunityName,
        onboarding.opportunityServiceCategoryName ?? '',
        pendingEngagements(onboarding),
      );
}

/** To: the opportunity contact, plus the quote signer when a different person signed. */
function defaultRecipients(onboarding: Onboarding): string {
  const emails: string[] = [];
  const push = (email: string | null): void => {
    const trimmed = (email ?? '').trim();
    if (!trimmed) return;
    if (emails.some((e) => e.toLowerCase() === trimmed.toLowerCase())) return;
    emails.push(trimmed);
  };
  push(onboarding.opportunityContactEmail);
  push(onboarding.quoteSignerEmail);
  return emails.join(', ');
}

export function SendWelcomeEmailModal({
  isOpen,
  onboarding,
  onClose,
}: SendWelcomeEmailModalProps): JSX.Element {
  const sendMutation = useSendWelcomeEmail();
  const regenMutation = useRegenerateWelcomeLink();
  const resetSend = sendMutation.reset;
  const resetRegen = regenMutation.reset;

  const [to, setTo] = useState('');
  const [cc, setCc] = useState(DEFAULT_CC);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const welcomeUrl = welcomeUrlOf(onboarding);
  const hasLink = Boolean(welcomeUrl);

  useEffect(() => {
    if (!isOpen) return;
    setTo(defaultRecipients(onboarding));
    setCc(DEFAULT_CC);
    setSubject(defaultSubject(onboarding));
    setBody(defaultBody(onboarding, welcomeUrlOf(onboarding)));
    resetSend();
    resetRegen();
    // Only reset on open / record change (avoids clobbering edits when the link is generated).
    // eslint config has no exhaustive-deps rule.
  }, [isOpen, onboarding.id, resetSend, resetRegen]);

  const handleGenerateLink = (): void => {
    regenMutation.mutate(onboarding.id, {
      onSuccess: (updated) => {
        const url = updated.welcomeToken
          ? `${window.location.origin}/w/${updated.welcomeToken}`
          : '';
        setBody(defaultBody(updated, url));
      },
    });
  };

  const handleSend = (): void => {
    sendMutation.mutate(
      {
        id: onboarding.id,
        input: { to: to.trim(), cc: cc.trim() || null, subject: subject.trim(), body },
      },
      { onSuccess: () => onClose() },
    );
  };

  const error =
    sendMutation.error instanceof ApiError
      ? sendMutation.error.message
      : sendMutation.error
        ? 'Failed to send the welcome email.'
        : null;

  const canSend = Boolean(to.trim() && subject.trim() && body.trim() && hasLink);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Send welcome email"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={sendMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSend} isLoading={sendMutation.isPending} disabled={!canSend}>
            Send email
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          Review the message before sending. It will be sent from your configured onboarding mailbox
          via Microsoft 365.
        </p>

        {hasLink ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
            <span className="text-slate-500">Welcome link included: </span>
            <span className="break-all font-medium text-slate-800">{welcomeUrl}</span>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
            <p>No active welcome link yet — generate one to include in the email.</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              isLoading={regenMutation.isPending}
              onClick={handleGenerateLink}
            >
              Generate welcome link
            </Button>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="To" value={to} onChange={(e) => setTo(e.target.value)} />
          <TextField label="CC" value={cc} onChange={(e) => setCc(e.target.value)} />
        </div>

        <TextField
          label="Subject"
          value={subject}
          maxLength={300}
          onChange={(e) => setSubject(e.target.value)}
        />

        <TextAreaField
          label="Message"
          value={body}
          rows={12}
          onChange={(e) => setBody(e.target.value)}
        />

        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
      </div>
    </Modal>
  );
}
